/***
*
*   useHelpFrame hook
*   Owns the cross-origin contract with the embedded help centre: the handshake,
*   inbound message validation, route reporting and the connection state.
*
*   The help centre posts nothing until it receives a valid host-hello from its
*   parent, and its listener mounts after hydration – so a single hello fired on
*   frame load loses the race. The hello is retried until it answers or the
*   handshake expires, and it answers every hello it accepts, so several `ready`
*   messages can arrive: handling is idempotent.
*
*   Nothing identifying is ever sent inward – the frame only ever learns the
*   host origin, which it needs to answer at all.
*
*   PROPS
*   frameRef: react ref to the <iframe> element
*   enabled: install nothing and stay idle when false
*   onRoute: ({ slug, title, resolved }) => void, fired on ready and route
*   onSupportRequest: ({ slug, title }) => void
*
**********/

import { useState, useEffect, useRef, useCallback } from 'react';
import { HELP_ORIGIN, HELP_PROTOCOL, HELP_PROTOCOL_VERSION,
  HELP_MESSAGE, normalizeSlug } from 'components/help/help';

// retry the hello until the frame's listener is mounted, then give up
const HANDSHAKE_INTERVAL = 400;
const HANDSHAKE_TIMEOUT = 8000;

// the only inbound types that are acted on – anything else is ignored
const INBOUND_TYPES = [

  HELP_MESSAGE.READY,
  HELP_MESSAGE.ROUTE,
  HELP_MESSAGE.SUPPORT_REQUEST

];

const IDLE_STATE = { status: 'connecting', resolved: true };

// the title is the frame's, and it ends up in a support email body. treat it
// like any other value from the other side of the boundary: strip anything
// that could restructure the message, and bound the length
const TITLE_MAX_LENGTH = 120;

function normalizeTitle(title){

  if (typeof title !== 'string')
    return null;

  // control characters first (they could restructure the mail body),
  // then squash the runs of whitespace that leaves behind
  const clean = title.replace(/[\u0000-\u001F\u007F]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!clean)
    return null;

  return clean.length > TITLE_MAX_LENGTH ?
    `${clean.slice(0, TITLE_MAX_LENGTH)}…` : clean;

}

export function useHelpFrame({ frameRef, enabled, onRoute, onSupportRequest }){

  const [state, setState] = useState(IDLE_STATE);

  // wrap the callbacks in refs so an inline arrow from the caller
  // doesn't tear down and reinstall the message listener on every render
  const onRouteRef = useRef(onRoute);
  const onSupportRequestRef = useRef(onSupportRequest);

  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const connectedRef = useRef(false);

  useEffect(() => { onRouteRef.current = onRoute }, [onRoute]);
  useEffect(() => { onSupportRequestRef.current = onSupportRequest }, [onSupportRequest]);

  const stopHandshake = useCallback(() => {

    if (intervalRef.current){

      clearInterval(intervalRef.current);
      intervalRef.current = null;

    }

    if (timeoutRef.current){

      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;

    }
  }, []);

  const sendHello = useCallback(() => {

    // never post to an unspecified origin, and never post to a frame
    // that isn't there – both would throw or leak
    if (!HELP_ORIGIN) return;
    const target = frameRef?.current?.contentWindow;
    if (!target) return;

    target.postMessage({

      protocol: HELP_PROTOCOL,
      version: HELP_PROTOCOL_VERSION,
      type: HELP_MESSAGE.HOST_HELLO,

      // the frame checks this against the real sender origin as an
      // anti-relay measure. it is the only field it is ever sent
      origin: window.location.origin

    }, HELP_ORIGIN);

  }, [frameRef]);

  useEffect(() => {

    if (!enabled || !HELP_ORIGIN){

      // reset if the frame was previously live – identity bails the re-render
      stopHandshake();
      connectedRef.current = false;
      setState(s => s.status === 'connecting' && s.resolved ? s : IDLE_STATE);
      return undefined;

    }

    const onMessage = event => {

      // 1. exact origin match only – no prefix, no wildcard, no parsing
      if (event.origin !== HELP_ORIGIN) return;

      // 2. and this frame's window specifically, not any other frame
      const frameWindow = frameRef?.current?.contentWindow;
      if (!frameWindow || event.source !== frameWindow) return;

      // 3. a plain object payload
      const data = event.data;
      if (!data || typeof data !== 'object' || Array.isArray(data)) return;

      // 4. our protocol, at a version we speak
      if (data.protocol !== HELP_PROTOCOL) return;
      if (data.version !== HELP_PROTOCOL_VERSION) return;

      // 5. a type we act on
      if (!INBOUND_TYPES.includes(data.type)) return;

      // re-validate the slug on the way in – the frame's value is
      // no more trusted than a url param, so never pass the raw one on
      const slug = normalizeSlug(data.slug);
      const title = normalizeTitle(data.title);

      // any valid message proves the frame answered, so stop greeting it.
      // idempotent: repeat hellos in flight produce repeat readys
      if (!connectedRef.current){

        connectedRef.current = true;
        stopHandshake();

      }

      if (data.type === HELP_MESSAGE.SUPPORT_REQUEST){

        setState(s => s.status === 'ready' ? s : { ...s, status: 'ready' });
        onSupportRequestRef.current?.({ slug: slug, title: title });
        return;

      }

      // ready and route both report the article the frame is showing.
      // an absent flag counts as resolved – don't cry not-found on silence
      const resolved = data.resolved !== false;

      setState(s => s.status === 'ready' && s.resolved === resolved ?
        s : { status: 'ready', resolved: resolved });

      onRouteRef.current?.({ slug: slug, title: title, resolved: resolved });

    }

    window.addEventListener('message', onMessage);

    return () => {

      window.removeEventListener('message', onMessage);
      stopHandshake();

    }
  }, [enabled, frameRef, stopHandshake]);

  // no timer may outlive the component
  useEffect(() => {

    return () => stopHandshake();

  }, [stopHandshake]);

  const armHandshake = useCallback(() => {

    if (!enabled || !HELP_ORIGIN) return;

    // re-arm from scratch on every load: a hard reload of the frame drops
    // the connection, which would otherwise leave it permanently silent
    stopHandshake();
    connectedRef.current = false;
    setState(s => s.status === 'connecting' && s.resolved ? s : IDLE_STATE);

    sendHello();

    intervalRef.current = setInterval(() => {

      if (connectedRef.current){

        stopHandshake();
        return;

      }

      sendHello();

    }, HANDSHAKE_INTERVAL);

    timeoutRef.current = setTimeout(() => {

      stopHandshake();

      // never answered – the view degrades to the support form
      if (!connectedRef.current)
        setState(s => ({ ...s, status: 'unavailable' }));

    }, HANDSHAKE_TIMEOUT);

  }, [enabled, sendHello, stopHandshake]);

  // arm once on mount as well as on every load. a frame the browser refuses
  // to load – a CSP violation, a blocked network – may never fire `load` at
  // all, and arming only from that handler would leave the page stuck on
  // 'connecting' with no notice and no escape hatch
  useEffect(() => {

    armHandshake();

  }, [armHandshake]);

  return {

    status: state.status,
    resolved: state.resolved,
    onFrameLoad: armHandshake

  }
}
